<template>
    <div>
        <div v-if="$route.params.mid">
            <team-member-view :member="memberDetails" :membersList="membersList"/>
        </div>
        <div v-else-if="team" v-loading="loading">
            <div class="row pl-3 my-team-header-buttons">
                    <div class="mr-2 btn-one-size btn-one-size-custom">
                        <team-drop-down/>
                    </div>
                    <fg-input class="btn-one-size mr-2">
                        <el-select size="large"
                                   class="select-primary btn-one-size-custom"
                                   :placeholder="$t('Time frame')"
                                   fullWidth
                                   @input="setMonthsFilter"
                                   :value="months">
                            <el-option v-for="option in monthsOptions"
                                       :value="option.value"
                                       :label="$t(option.label)"
                                       :key="option.value">
                            </el-option>
                        </el-select>
                    </fg-input>
                    <p-button round class="mt-0 mr-2 btn-one-size-custom" type="primary" @click="selectPDFExport">
                        {{ $t('Export as PDF') }}
                    </p-button>
            </div>
            <div>
                <div class="row">
                    <div v-if="checkPackage(['advanced', 'premium'])" class="col-lg-6 col-sm-12">
                        <basic-chart :title="$t('Team Trend')" :data="overAllTrendDataset"
                                      :badge-value="overAllAverage"
                                      icon="fa fa-exclamation-triangle"
                                      link="true"
                                      :isUserOwner="isUserOwner"
                                      :alertActive="overAllTrendDataset.alertActive"
                                      @clicked="showAlertConfig('accumulated')"
                                      />
                    </div>
                    <div v-else class="col-lg-12 col-sm-12">
                        <basic-chart :title="$t('Team Trend')" :data="overAllTrendDataset"
                                      :badge-value="overAllAverage"
                                      />
                    </div>
                    <div v-if="checkPackage(['premium'])" class="col-lg-6 col-sm-12">
                        <div class="card card-diagram">
                            <div class="card-header" v-if="$slots.header">
                                <slot name="header"/>
                            </div>
                            <div class="card-body">
                                <voronoi-diagram
                                        v-if="voronoiDataset.data.length > 0"
                                        class="mt-1"
                                        chart-id="voronoi-chart"
                                        :height="256"
                                        :extra-options="{ responsive: true, maintainAspectRatio: false }"
                                        :datasets="[ voronoiDataset ]"
                                />
                            </div>
                            <div class="card-footer">
                                <hr>
                                <div class="row">
                                    <div class="col-sm-7" v-bind:style="isUserOwner ? 'margin-bottom: 0.6rem!important; margin-top: 0.6rem!important;' : ''">
                                        <div class="footer-title">{{$t('Team Engagement Compass')}}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="checkPackage(['premium', 'advanced'])" class="row">
                    <div class="col-sm-12">
                        <div class="card card-diagram">
                            <div class="card-header" v-if="$slots.header">
                                <slot name="header"/>
                            </div>
                            <div class="card-body">
                                <wordcloud class="word-cloud-height"
                                           :data="wordCloudData"
                                           nameKey="word"
                                           valueKey="count"
                                           :color="wordCloudColors"
                                           :showTooltip="false"
                                           :wordClick="wordClickHandler"
                                           :rotate="{from: 0, to: 0, numOfOrientation: 0}"
                                />
                            </div>
                            <div class="card-footer">
                                <hr>
                                <div class="row">
                                    <div class="col-sm-7">
                                        <div class="footer-title">{{$t('Wordcloud')}}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <layout-wrapper v-for="(dataset, i) in teamAnswersDatasets" :key="`teamAnswersDataset_${i}`"
                                    :length="teamAnswersDatasets.length">
                        <template slot="body">
                            <basic-chart v-if="checkPackage(['advanced', 'premium'])" 
                                         :title="dataset.title | languageString" :data="dataset"
                                         :badge-value="dataset.average"
                                         icon="fa fa-exclamation-triangle"
                                         link="true"
                                         :isUserOwner="isUserOwner"
                                         :alertActive="dataset.alertActive"
                                         @clicked="showAlertConfig('question', dataset.question)"
                                         />
                            <basic-chart v-else 
                                         :title="dataset.title | languageString" :data="dataset"
                                         :badge-value="dataset.average"
                                         />
                        </template>
                    </layout-wrapper>
                </div>
                <div class="row">
                    <div class="col-sm-12">
                        <team-member-list v-model="membersOverview" :isUserOwner="isUserOwner">
                            <template slot="header">
                                <h5>{{ this.$t('{team} members at a glance', { team: team.title })}}</h5>
                            </template>
                        </team-member-list>
                    </div>
                </div>
            </div>
        </div>

        <modal v-if="showPDFExportDialog" v-model="showPDFExportDialog" :title="$t('Export as PDF')" :submit="exportAsPdf" :cancel="cancelExport" :submit-text="`${$t('Export')}`" v-loading="pdfExportLoading">
            <template slot="body">
              <fg-input :label="$t('PDF Typ')" name="pdftype" required
                        v-model="pdfType">
                  <el-select size="large"
                              class="select-primary"
                              v-model="pdfType"
                              :placeholder="placeholder ? placeholder : $t('Type for search...')"
                              @change="pdfEndQuery = null; pdfStartQuery = null">
                      <el-option v-for="option in pdfTypeOptions"
                                  :value="option"
                                  :label="$t(pdfTypeNames[option])"
                                  :key="option">
                      </el-option>
                  </el-select>
              </fg-input>

              <fg-input :label="$t('From Query')" name="range" v-if="pdfType === 'range'" required>
                <el-select size="large"
                              class="select-primary"
                              v-model="pdfStartQuery"
                              :placeholder="placeholder ? placeholder : $t('Type for search...')"
                              @change="pdfEndQuery = null">
                      <el-option v-for="option in teamQueryDates"
                                  :value="option._id"
                                  :label="option.evaluationTime | formatDate"
                                  :key="option._id">
                      </el-option>
                  </el-select>
              </fg-input>
              <fg-input :label="$t('To Query')" name="range" v-if="pdfType === 'range'" required>
                <el-select size="large"
                              class="select-primary"
                              v-model="pdfEndQuery"
                              :placeholder="placeholder ? placeholder : $t('Type for search...')"
                              :disabled="pdfStartQuery === null">
                      <el-option v-for="option in getQueryDatesFromStart"
                                  :value="option._id"
                                  :label="option.evaluationTime | formatDate"
                                  :key="option._id">
                      </el-option>
                  </el-select>
              </fg-input>
            </template>
        </modal>

        <modal v-if="showAlertEditModal" v-model="showAlertEditModal" :title="$t('Alert Values')" :submit="saveAlertEdit" :cancel="cancelAlertEdit" :submit-text="`${$t('Save')}`" v-loading="alertEditLoading">
            <template slot="body">
              <el-row type="flex" justify="space-between">
                <el-col :span="12">
                  <span>{{ this.$t('Alert at') }}</span>
                </el-col>
                <el-col :span="12" style="text-align: right;">
                  <span>{{ this.$t('Target at') }}</span>
                </el-col>
              </el-row>
              <el-row type="flex" justify="space-between" class="mt-1">
                <el-col :span="12">
                  <el-input-number 
                    v-model="alertInputLowerValue" 
                    controls-position="right" 
                    :min="-0.1" 
                    :max="10" 
                    :precision="1" 
                    :step="0.1"
                    size="small"
                    ref="alertEditLowerInput"
                    @change="alertEditLowerValueInputChanged"
                  ></el-input-number>
                  <p-button 
                    round 
                    class="mt-0 mr-0 mb-0 ml-1" 
                    type="primary" 
                    size="sm" 
                    icon 
                    @click="alertEditRemoveLowerValue"
                    >
                    <i class="fa fa-times" />
                  </p-button>
                </el-col>
                <el-col :span="12" style="text-align: right;">
                  <el-input-number 
                    v-model="alertInputUpperValue" 
                    controls-position="right" 
                    :min="0" 
                    :max="10.1" 
                    :precision="1" 
                    :step="0.1"
                    size="small"
                    ref="alertEditUpperInput"
                    @change="alertEditUpperValueInputChanged"
                  ></el-input-number>
                  <p-button 
                    round 
                    class="mt-0 mr-0 mb-0 ml-1" 
                    type="primary" 
                    size="sm" 
                    icon 
                    @click="alertEditRemoveUpperValue"
                    >
                    <i class="fa fa-times" />
                  </p-button>
                </el-col>
              </el-row>
              <el-row type="flex" justify="space-between">
                <el-col :span="24">
                  <el-slider
                    ref="alertSlider"
                    v-model="alertModal.sliderValue"
                    range
                    :show-tooltip="false"
                    :min="-0.1"
                    :max="10.1"
                    :step="0.1"
                    >
                  </el-slider>
                </el-col>
              </el-row>
            </template>
        </modal>
        
    </div>
</template>

<script>
  import { Button, Select, Option, DatePicker, Slider, Row, Col } from 'element-ui'
  import wordcloud from 'vue-wordcloud'
  import BasicChart from '../../Dashboard/Components/BasicChart'
  import Modal from '../../Dashboard/Components/BasicModal'
  import VoronoiDiagram from '../../UIComponents/Charts/VoronoiDiagram'
  import LayoutWrapper from '../../UIComponents/LayoutWrapper'
  import TeamDropDown from './TeamDropDownList'
  import TeamMemberList from './TeamMemberList'
  import TeamMemberView from './TeamMemberView'
  import { mapActions, mapGetters, mapState } from 'vuex'
  import values from 'lodash/values'
  import { colors, getBackgroundColor, getSliderGradient } from '../../../util/colors'
  import moment from "moment"
  import InputNumber from "../../UIComponents/Inputs/CustomerInputNumber"

  // We need this here to "inject" it into the daterange picker
  let packageType = 'basic'

  export default {
    name: 'TeamView',
    components: {
      [Button.name]: Button,
      [Select.name]: Select,
      [Option.name]: Option,
      [DatePicker.name]: DatePicker,
      [Slider.name]: Slider,
      [InputNumber.name]: InputNumber,
      [Row.name]: Row,
      [Col.name]: Col,
      wordcloud,
      BasicChart,
      VoronoiDiagram,
      LayoutWrapper,
      TeamDropDown,
      TeamMemberList,
      TeamMemberView,
      Modal
    },
    data () {
      return {
        wordCloudColors: [colors[8]],
        loading: true,
        pdfExportLoading: false,
        showPDFExportDialog: false,
        pdfType: 'last',
        pdfTypeOptions: ['last'],
        pdfTypeNames: {
          'last': 'Last Query',
          'range': 'Period'
        },
        pdfStartQuery: null,
        pdfEndQuery: null,
        showAlertEditModal: false,
        alertEditLoading: false,
        alertModal: {
          type: 'question',
          questionnaire: null,
          question: null,
          sliderValue: [null, null]
        }
      }
    },
    mounted () {
      this.init()
    },
    computed: {
      ...mapGetters({
        questionnaire: 'user/teams/getQuestionnaire',
        overAllTrendDataset: 'user/teams/overAllTrendDataset',
        overAllAverage: 'user/teams/overAllAverage',
        voronoiDataset: 'user/teams/voronoiDataset',
        wordCloudData: 'user/teams/wordCloudData',
        teamAnswersDatasets: 'user/teams/teamAnswersDatasets',
        isUserOwner: 'user/teams/isUserOwner'
      }),
      ...mapState('user/teams', {
        teamOverview: state => state.teamOverview,
        teamQueryDates: state => state.teamQueryDates,
        months: state => state.months,
        monthsOptions: state => state.monthsOptions
      }),
      memberDetails: function () {
        if (this.membersOverview.length > 0 && this.$route.params.mid) {
          return this.teamOverview.userAccumulations[this.$route.params.mid]
        }
        return null
      },
      membersList: function () {
        if (this.membersOverview.length > 0 && this.$route.params.mid) {
          return Object.values(this.teamOverview.userAccumulations).sort(this.sortUsers)
        }
        return null
      },
      team: function () {
        return this.teamOverview && this.teamOverview.team || {}
      },
      membersOverview: function () {
        return this.teamOverview ? values(this.teamOverview.userAccumulations).sort(this.sortUsers) : []
      },
      breadcrumb: function () {
        const breadcrumb = []
        const { qid, tid, mid } = this.$route.params

        if (qid && this.questionnaire) {
          breadcrumb.push({
            name: 'TeamQuestionnaires',
            label: this.$options.filters.languageString(this.questionnaire.title)
          })
        }
        if (tid && this.team) {
          breadcrumb.push({ name: 'TeamSelection', params: { qid }, label: this.team.title })
        }
        breadcrumb.push({ name: 'TeamView', label: 'Results' })

        return breadcrumb
      },
      getQueryDatesFromStart: function () {
        if (!this.pdfStartQuery) return this.teamQueryDates

        for (const i in this.teamQueryDates) {
          if (this.teamQueryDates[i]._id === this.pdfStartQuery) {
            let ret = this.teamQueryDates.slice(0, Number(i)+1)
            ret = ret.sort( (a, b) => {
              const ad = new Date(a.evaluationTime).getTime()
              const bd = new Date(b.evaluationTime).getTime()
              if (ad < bd) {
                return -1
              } else if (ad > bd) {
                return 1
              } else {
                return 0
              }
            })
            return ret
          }
        }
      },
      alertInputLowerValue: {
        get: function() {
          if (this.$refs.alertEditLowerInput) {
            if (this.alertModal.sliderValue[0] < 0) {
              this.$refs.alertEditLowerInput.$children[0].$refs.input.value = ''
            }
          }

          return this.alertModal.sliderValue[0]
        },
        set: function (val) {
          this.alertModal.sliderValue[0] = val
        }
      },
      alertInputUpperValue: {
        get: function() {
          if (this.$refs.alertEditLowerInput) {
            if (this.alertModal.sliderValue[1] > 10) {
              this.$refs.alertEditUpperInput.$children[0].$refs.input.value = ''
            }
          }

          return this.alertModal.sliderValue[1]
        },
        set: function (val) {
          this.alertModal.sliderValue[1] = val
        }
      }
    },
    methods: {
      ...mapActions('user/teams', ['getTeamOverview', 'getTeamQueryDates', 'storeTeamAlert', 'setMonthsFilter', 'initPackageMonthsOptions']),
      init () {
        packageType = this.$auth.user.customer.settings.contract.packageType
        this.initPackageMonthsOptions(this.$auth.user.customer.settings.contract.packageType)
        this.getTeamOverview({
          questionnaireId: this.$route.params.qid,
          teamId: this.$route.params.tid,
          months: this.months
        })
        if (this.checkPackage(['advanced', 'premium'])) {
          if (this.pdfTypeOptions.indexOf('range') === -1) this.pdfTypeOptions.push('range')
        }
        setTimeout(() => {
          this.loading = false
        }, 2000)
      },
      sortUsers (a, b) {
        const an = `${a.user.firstName} ${a.user.lastName}`
        const bn = `${b.user.firstName} ${b.user.lastName}`
        if (an < bn) {
          return -1
        } else if (an > bn) {
          return 1
        } else {
          return 0
        }
      },
      async exportAsPdf () {
        const formatDate = (date) => moment(date).format('YYYY-MM-DD')
        this.pdfExportLoading = true
        const { qid, tid } = this.$route.params
        let path = `/api/myTeam/downloadTeamPDFExport/${qid}/${tid}/${this.months}?lang=${localStorage.getItem('locale')}`
        if (this.pdfStartQuery !== null && this.pdfEndQuery !== null) {
          path += `&start=${this.pdfStartQuery}&end=${this.pdfEndQuery}`
        }
        
        this.$http({
          method: 'get',
          url: path,
          responseType: 'arraybuffer'
        })
        .then(response => {
          this.pdfExportLoading = false
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          let fileName = `MyTeam_${moment().format('L')}_${this.team.title}_${this.$options.filters.languageString(this.questionnaire.title)}`
          if (this.pdfStartQuery !== null && this.pdfEndQuery !== null) {
            const startDate = this.teamQueryDates.find( x => x._id === this.pdfStartQuery)
            const endDate = this.teamQueryDates.find( x => x._id === this.pdfEndQuery)
            fileName += `_${formatDate(startDate.evaluationTime)}_${formatDate(endDate.evaluationTime)}.pdf`
          } else {
            fileName += `_${this.months}-Months.pdf`
          }
          link.setAttribute('download', fileName) //or any other extension
          document.body.appendChild(link)
          link.click()
          this.showPDFExportDialog = false
          this.loading = false
        })
        .catch(() => {
          // console.log('error occured')
          this.pdfExportLoading = false
          this.loading = false
        })
      },
      wordClickHandler (name, value, vm) {
        // console.log('wordClickHandler', name, value, vm)
      },
      checkPackage (allowedPackages) {
        return allowedPackages.includes(this.$auth.user.customer.settings.contract.packageType)
      },
      selectPDFExport: function() {
        this.getTeamQueryDates({
          questionnaireId: this.$route.params.qid,
          teamId: this.$route.params.tid
        })
        this.pdfStartQuery = null
        this.pdfEndQuery = null
        this.showPDFExportDialog = true
      },
      cancelExport: function () {
        this.showPDFExportDialog = false
      },
      showAlertConfig (type, question) {
        this.showAlertEditModal = true

        this.$nextTick( () => {
          this.alertModal.type = type
          this.alertModal.question = null
          this.alertModal.questionnaire = this.teamOverview.questionnaire
          this.alertModal.sliderValue[0] = -0.1
          this.alertModal.sliderValue[1] = 10.1
          // Load the query data
          if (type === 'accumulated') {
            for (const alert of this.teamOverview.team.alerts) {
              if (alert.type === 'accumulated' && String(alert.questionnaire) === String(this.teamOverview.questionnaire._id)) {
                this.alertModal.sliderValue[0] = alert.lowerValue
                this.alertModal.sliderValue[1] = alert.upperValue
              }
            }
          } else {
            this.alertModal.question = question
            for (const alert of this.teamOverview.team.alerts) {
              if (alert.type === 'question' && String(alert.questionnaire) === String(this.teamOverview.questionnaire._id) &&
                String(alert.question) === String(question)) {
                this.alertModal.sliderValue[0] = alert.lowerValue
                this.alertModal.sliderValue[1] = alert.upperValue
              }
            }
          }
          this.$refs.alertSlider.firstValue = this.alertModal.sliderValue[0]
          this.$refs.alertSlider.secondValue = this.alertModal.sliderValue[1]
          this.$nextTick( () => this.alertEditSetSliderColor())
        })
      },
      saveAlertEdit () {
        try {
          this.storeTeamAlert({
            team: `${this.$route.params.tid}`, 
            type: this.alertModal.type, 
            questionnaire: `${this.alertModal.questionnaire._id}`, 
            question: this.alertModal.question ? `${this.alertModal.question}` : null, 
            lowerValue: this.alertModal.sliderValue[0], 
            upperValue: this.alertModal.sliderValue[1]
          })

          // Also store locally

        } catch (error) {

        }
        
        this.showAlertEditModal = false
      },
      cancelAlertEdit () {
        this.alertModal.sliderValue[0] = -0.1
        this.alertModal.sliderValue[1] = 10.1
        this.$nextTick( () => this.showAlertEditModal = false)
        
      },
      alertEditSetSliderColor() {
        if (!this.$refs.alertSlider) return

        this.$refs.alertSlider.$children[0].$children[0].$el.style.setProperty('border', `solid 2px ${getBackgroundColor(this.alertModal.sliderValue[0])}`)
        if (this.alertModal.sliderValue[0] < 0) {
          this.$nextTick( () => {
            this.$refs.alertEditLowerInput.$children[0].$refs.input.value = ''
          })
          this.$refs.alertSlider.$children[0].$children[0].$el.style.setProperty('border', `solid 2px #bfbfbf`)
        }

        this.$refs.alertSlider.$children[1].$children[0].$el.style.setProperty('border', `solid 2px ${getBackgroundColor(this.alertModal.sliderValue[1])}`)
        if (this.alertModal.sliderValue[1] > 10) {
          this.$nextTick( () => {
            this.$refs.alertEditUpperInput.$children[0].$refs.input.value = ''
          })
          this.$refs.alertSlider.$children[1].$children[0].$el.style.setProperty('border', `solid 2px #bfbfbf`)
        }
        this.$refs.alertSlider.$refs.slider.childNodes[0].style.setProperty('background', getSliderGradient(this.alertModal.sliderValue[0], this.alertModal.sliderValue[1]))
      },
      alertEditLowerValueInputChanged (val) {
        this.$refs.alertSlider.firstValue = Number(val)
      },
      alertEditUpperValueInputChanged (val) {
        this.$refs.alertSlider.secondValue = Number(val)
      },
      alertEditRemoveLowerValue() {
        this.$refs.alertSlider.firstValue = -0.1
      },
      alertEditRemoveUpperValue () {
        this.$refs.alertSlider.secondValue = 10.1
      }
    },
    watch: {
      months (val) {
        if (val) {
          this.init()
        }
      },
      breadcrumb () {
        this.$store.commit('breadcrumb/SET_ROUTE_DATA', {
          path: this.$route.path,
          breadcrumb: this.breadcrumb
        }, { root: true })
      },
      async '$route.params.tid' (val) {
        if (val.length > 1) {
          this.loading = true
          this.init()
        }
      },
      "alertModal.sliderValue" (val) {
        this.alertEditSetSliderColor()
      }
    }
  }
</script>

<style lang="scss">
    .card-diagram {
        .card-body {
            margin-top: 14px;
        }

        .card-footer {
            white-space: nowrap;
        }

        hr {
            margin-top: 0;
        }
    }

    .custom-button {
        display: inline-block;
        color: black;
        height: 40px;
        padding: 10px 0 0;
        min-width: 42px;
        border-radius: 4px;
    }

    .inline {
        display: inline-block;
    }

    .wordCloud .text {
        cursor: pointer;
    }

    .word-cloud-height {
        height: 275px !important;
        svg {
            touch-action: auto !important;
        }
    }
    .my-team-header-buttons {
      .btn-one-size {
        width: 15rem;
        @media (max-width: 768px){
          width: 100%;
        }
      }
    }

    @media (max-width: 768px) {
      .btn-one-size {
        width: 100%;
        padding-left: 0px;
        padding-right: 0px;
      }
    }
</style>
